import { Link } from "react-router";
import { api } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { fmtPct, STUDY_STATUS_LABELS } from "../../lib/format.js";
import { Alert, Badge, ErrorAlert, PageHeader, Spinner, Stat, SyntheticBanner } from "../../components/ui/index.js";

export function DashboardPage() {
  const studies = useAsync(() => api.listStudies(), []);
  const list = studies.data ?? [];
  const totals = list.reduce(
    (acc, s) => {
      acc.real += s.sessionCounts.webgazer.total;
      acc.realDone += s.sessionCounts.webgazer.completed;
      acc.synth += s.sessionCounts.synthetic.total;
      acc.synthDone += s.sessionCounts.synthetic.completed;
      acc.warnings += s.qualityWarnings;
      return acc;
    },
    { real: 0, realDone: 0, synth: 0, synthDone: 0, warnings: 0 },
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Panel" description="Resumen de estudios, sesiones reales y sintéticas, completitud y advertencias de calidad.">
        <Link to="/estudios" className="btn-primary">
          Gestionar estudios
        </Link>
      </PageHeader>
      {totals.synth > 0 && <SyntheticBanner />}
      <ErrorAlert error={studies.error} />
      {studies.loading && !studies.data ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-tour="dash-stats">
            <Stat label="Estudios" value={list.length} />
            <Stat label="Sesiones reales" value={totals.real} />
            <Stat label="Completitud real" value={totals.real ? fmtPct(totals.realDone / totals.real, 0) : "—"} />
            <Stat label="Sesiones sintéticas" value={totals.synth} hint="Solo demostración" />
            <Stat label="Advertencias de calidad" value={totals.warnings} />
          </div>
          {list.length === 0 ? (
            <Alert tone="info" title="Aún no hay estudios">
              Crea un estudio en «Estudios» o ejecuta <code>pnpm db:seed</code> para cargar datos de demostración.
            </Alert>
          ) : (
            <div className="card overflow-x-auto p-0" data-tour="dash-table">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Estudio</th>
                    <th className="px-4 py-2">Estado</th>
                    <th className="px-4 py-2">Imágenes</th>
                    <th className="px-4 py-2">Reales (completadas / total)</th>
                    <th className="px-4 py-2">Completitud real</th>
                    <th className="px-4 py-2">Sintéticas</th>
                    <th className="px-4 py-2">Advertencias</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((s) => {
                    const r = s.sessionCounts.webgazer;
                    const sy = s.sessionCounts.synthetic;
                    return (
                      <tr key={s.id}>
                        <td className="px-4 py-2">
                          <Link to={`/estudios/${s.id}/resultados`} className="font-medium text-sky-800 hover:underline">
                            {s.title}
                          </Link>
                          <div className="text-xs text-slate-500">Código {s.code}</div>
                        </td>
                        <td className="px-4 py-2">
                          <Badge tone={s.status === "active" ? "success" : "neutral"}>{STUDY_STATUS_LABELS[s.status]}</Badge>
                        </td>
                        <td className="px-4 py-2">{s.imageCount}</td>
                        <td className="px-4 py-2">
                          {r.completed} / {r.total}
                        </td>
                        <td className="px-4 py-2">{r.total ? fmtPct(r.completed / r.total, 0) : "—"}</td>
                        <td className="px-4 py-2">
                          {sy.total > 0 ? (
                            <span className="inline-flex items-center gap-2">
                              <Badge tone="synthetic">SINTÉTICO</Badge> {sy.completed} / {sy.total}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-2">{s.qualityWarnings > 0 ? <Badge tone="warning">{s.qualityWarnings}</Badge> : "0"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
